import { API_URL } from '../../constants';
import { getRequest } from '../../utils/requestService';
import { getDispatch } from '../../../spa/store';

export type TEndlessBookToken = {
  token: string;
  exp: number; // unix seconds
  bookHost: string;
};

// Generation settings of a prose book, as endless-book's create / regenerate take them.
export type TBookSettings = {
  genre: string;
  styleAuthor: string;
  plotSeed: string;
  nonfiction: boolean;
  keywords: string;
  targetLang: string;
  nativeLang: string;
};

export type TBookVoice = {
  name: string; // Google voice id, e.g. "ru-RU-Chirp3-HD-Kore"
  gender: string;
};

// endless-book's GET /api/voices: languages by name, voices and the user's choices by language code.
export type TBookVoices = {
  languages: { name: string, code: string }[];
  voices: Record<string, TBookVoice[]>;
  prefs: Record<string, string>;
};

export class EndlessBookRest {

  private static TOKEN_URL = API_URL + '/endlessBook/token';

  // Bearer for calling endless-book directly from the browser (the embedded reader, regenerate).
  public static async getToken(): Promise<TEndlessBookToken> {
    const dispatch = getDispatch();
    return await dispatch(getRequest(EndlessBookRest.TOKEN_URL));
  }

  public static async regenerate(bookHost: string, token: string, bookId: number, settings: TBookSettings): Promise<void> {
    await EndlessBookRest.request(bookHost, token, `/books/${bookId}/regenerate`, settings);
  }

  // The book's translation language only — the text stays; glosses come in the new language as read.
  public static async setTranslationLang(bookHost: string, token: string, bookId: number, nativeLang: string): Promise<void> {
    await EndlessBookRest.request(bookHost, token, `/books/${bookId}/translation-langs`, { nativeLang });
  }

  public static async getVoices(bookHost: string, token: string): Promise<TBookVoices> {
    return await EndlessBookRest.request(bookHost, token, '/voices');
  }

  // A short spoken sample as a data: URI.
  public static async getVoiceSample(bookHost: string, token: string, languageName: string, voiceName: string): Promise<string> {
    const res = await EndlessBookRest.request(bookHost, token, '/voices/sample', { languageName, voiceName });
    return res.audio;
  }

  // voiceName '' = automatic. Per language: applies to every book of the user in that language.
  public static async setVoice(bookHost: string, token: string, languageCode: string, voiceName: string): Promise<Record<string, string>> {
    const res = await EndlessBookRest.request(bookHost, token, '/settings/voice', { languageCode, voiceName });
    return res.prefs || {};
  }

  // Straight to endless-book with the Bearer — fetch rather than axios, whose global withCredentials
  // would make this a credentialed request that book's wildcard CORS refuses. POST when there's a body.
  private static async request(bookHost: string, token: string, path: string, body?: object): Promise<any> {
    const res = await fetch(`${bookHost}/api${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch((): null => null);
    if (!res.ok) throw new EndlessBookError(json?.error || `HTTP ${res.status}`, res.status);
    return json;
  }

}

export class EndlessBookError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}
